import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { IBusinessPolicyContractor } from "./businessPolicyContractor.type";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

export const FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_FAILED";

export const fetchBusinessPolicyContractorListProgress = () =>
  action(FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_PROGRESS);
export const fetchBusinessPolicyContractorListSuccess = (
  data: IBusinessPolicyContractor[]
) => action(FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_SUCCESS, { data });
export const fetchBusinessPolicyContractorListFailed = () =>
  action(FETCH_BUSINESS_POLICY_CONTRACTOR_LIST_FAILED);

export const fetchBusinessPolicyContractorListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyContractorListProgress());

      let finalUrl = `/policy/get-policy-business-contractor?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyContractor[] = res.data.data;
      dispatch(fetchBusinessPolicyContractorListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyContractorListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Contractor list fetch. Keyed by `lead_id` (never `customer_policy_id`),
// routed through the centralized endpoint map. On failure/timeout it flips the list to
// the Failed load state (retry surfaced by the sub-tab) and shows an error message
// (Requirements 4.1, 4.5, 4.9). Mirrors `fetchLeadBusinessRiskListAsync`.
export const fetchLeadContractorListAsync =
  (
    scope: ILeadScheduleScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyContractorListProgress());
      const request = buildBinderRequest(
        "contractor",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyContractor[] = res.data.data;
      dispatch(fetchBusinessPolicyContractorListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyContractorListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Contractor binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_CONTRACTOR_PROGRESS =
  "FETCH_BUSINESS_POLICY_CONTRACTOR_PROGRESS";
export const FETCH_BUSINESS_POLICY_CONTRACTOR_SUCCESS =
  "FETCH_BUSINESS_POLICY_CONTRACTOR_SUCCESS";
export const FETCH_BUSINESS_POLICY_CONTRACTOR_FAILED =
  "FETCH_BUSINESS_POLICY_CONTRACTOR_FAILED";

export const fetchBusinessPolicyContractorProgress = () =>
  action(FETCH_BUSINESS_POLICY_CONTRACTOR_PROGRESS);
export const fetchBusinessPolicyContractorSuccess = (
  data: IBusinessPolicyContractor
) => action(FETCH_BUSINESS_POLICY_CONTRACTOR_SUCCESS, { data });
export const fetchBusinessPolicyContractorFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_CONTRACTOR_FAILED, { errorMessage });

export const fetchBusinessPolicyContractorAsync =
  (
    customerPolicyId: number,
    CONTRACTORNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyContractorProgress());
      const res = await api.get(
        `/policy/get-policy-business-contractor?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${CONTRACTORNumber}`
      );
      const data: IBusinessPolicyContractor[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyContractorSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyContractorFailed(
            "Oops! We couldn't find any records associated with your contractor number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyContractorFailed("Something went to be wrong!")
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertBusinesPolicyContractorAsync =
  (
    restrutanr: IBusinessPolicyContractor,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post(`/policy/edit-policy-business-contractor`, restrutanr);
      dispatch(
        showMessage({
          type: "success",
          message: "Contractor saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped single Contractor fetch (by incremental number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map. On failure/timeout it
// flips the record to the Failed load state so the sub-tab can surface the error +
// retry. Mirrors `fetchLeadEquipmentAsync`.
export const fetchLeadContractorAsync =
  (
    scope: ILeadScheduleScope,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyContractorProgress());
      const request = buildBinderRequest(
        "contractor",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=incremental_number&value=${incrementNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyContractor[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyContractorSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyContractorFailed(
            "Oops! We couldn't find any records associated with your contractor number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyContractorFailed("Something went to be wrong!")
      );
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Contractor binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

// Guard preventing a duplicate lead-scoped save from being dispatched while a save for
// the same record is already in flight.
let leadContractorSaveInFlight = false;

// Feature: lead-binder-additional-tabs
// Lead-scoped Contractor save. Persists with `lead_id` and NO `customer_policy_id`
// (the scope keys come from `buildBinderRequest`; the customer-policy identifiers are
// stripped from the record). A local in-flight guard prevents a duplicate save for the
// same record while one is already running. On success it re-fetches the list by the
// same `lead_id` (Requirements 4.2, 4.3). On failure/timeout it surfaces an error; the
// caller retains the entered input (Requirement 4.6). Mirrors
// `addLeadBusinessEquipmentAsync`.
export const addLeadBusinessContractorAsync =
  (
    data: IBusinessPolicyContractor,
    scope: ILeadScheduleScope,
    onCallback: (isSuccess: boolean) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadContractorSaveInFlight) {
      return;
    }
    leadContractorSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "contractor",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const { customer_id, customer_policy_id, ...rest } =
        data as IBusinessPolicyContractor & {
          customer_id?: unknown;
          customer_policy_id?: unknown;
        };
      await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Contractor saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the list by the same lead_id after a successful save.
      dispatch(fetchLeadContractorListAsync(scope, status));
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Contractor could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadContractorSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_BUSINESS_POLICY_CONTRACTOR =
  "CLEAR_BUSINESS_POLICY_CONTRACTOR";
export const CLEAR_BUSINESS_POLICY_CONTRACTOR_STATE =
  "CLEAR_BUSINESS_POLICY_CONTRACTOR_STATE";

export const clearBusinessPolicyContractorState = () =>
  action(CLEAR_BUSINESS_POLICY_CONTRACTOR_STATE);

export const clearBusinessPolicyContractor = () =>
  action(CLEAR_BUSINESS_POLICY_CONTRACTOR);
